import React, { useState, useEffect, useMemo } from 'react';
import { CheckCircle, XCircle, AlertTriangle, RefreshCw, FileText, TrendingUp, Download, Flag } from 'lucide-react';

const DCAAAuditPage = () => {
  const [scheduleHList, setScheduleHList] = useState([]);
  const [selectedScheduleH, setSelectedScheduleH] = useState('');
  const [auditResults, setAuditResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [baselineStatus, setBaselineStatus] = useState(null);
  const [reparseStatus, setReparseStatus] = useState(null);
  const [reparseLoading, setReparseLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());

  const reparseAdequacyChecks = async () => {
    setReparseLoading(true);
    setReparseStatus(null);
    try {
      const res = await fetch(`${BACKEND_URL}/audit/adequacy-checks/reparse`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reparse failed');
      setReparseStatus({ type: 'success', message: `Refreshed ${data.adequacy_check_count} checks. Re-run audit to see changes.` });
    } catch (e) {
      setReparseStatus({ type: 'error', message: e.message });
    } finally {
      setReparseLoading(false);
    }
  };

  const toggleExpanded = (key) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const BACKEND_URL = '/api';  // Use relative path for API calls

  // Load available Schedule H calculations
  useEffect(() => {
    fetchScheduleHList();
  }, []);

  const fetchScheduleHList = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/schedule-h/list`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setScheduleHList(data.calculations || []);
      
      // Auto-select most recent
      if (data.calculations && data.calculations.length > 0) {
        setSelectedScheduleH(data.calculations[0].schedule_h_id);
      }
    } catch (err) {
      setError('Failed to load Schedule H list: ' + err.message);
      console.error('Schedule H list error:', err);
    }
  };

  const runAudit = async () => {
    if (!selectedScheduleH) {
      setError('Please select a Schedule H calculation');
      return;
    }

    setLoading(true);
    setError('');
    setBaselineStatus(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/audit/dcaa-audit/${selectedScheduleH}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error('Audit failed');
      }
      
      const data = await response.json();
      setAuditResults(data);
    } catch (err) {
      setError('Failed to run audit: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const markBaseline = async () => {
    if (!auditResults?.audit_id) return;
    try {
      const res = await fetch(`${BACKEND_URL}/audit/dcaa-audit/${auditResults.audit_id}/baseline`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setBaselineStatus({ type: 'success', message: `Baseline saved (${data.audit_id})` });
    } catch (e) {
      setBaselineStatus({ type: 'error', message: 'Failed to save baseline: ' + e.message });
    }
  };

  const exportCSV = async () => {
    if (!auditResults?.audit_id) return;
    try {
      const res = await fetch(`${BACKEND_URL}/audit/dcaa-audit/${auditResults.audit_id}/export.csv`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${auditResults.audit_id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('Failed to export CSV: ' + e.message);
    }
  };

  const getStatusColor = (status) => {
    return status === 'PASS' ? 'text-green-600' : 'text-red-600';
  };

  const getStatusBg = (status) => {
    return status === 'PASS' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
  };

  const getStatusIcon = (status) => {
    return status === 'PASS' ? 
      <CheckCircle className="h-5 w-5 text-green-600" /> : 
      <XCircle className="h-5 w-5 text-red-600" />;
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatPercentage = (value) => {
    if (value === undefined || value === null) return '0.00%';
    return `${(value * 100).toFixed(4)}%`;
  };

  const normalizeScheduleLabel = (rawSchedule, checkId, description) => {
    const raw = String(rawSchedule || '').trim().toUpperCase();
    if (raw) {
      const withoutPrefix = raw.replace(/^SCHEDULE\s*/, '');
      const rawMatch = withoutPrefix.match(/[A-Z0-9]/);
      if (rawMatch) return rawMatch[0];
    }

    const checkKey = String(checkId || '').trim();
    const keyMatch = checkKey.match(/^([A-Za-z])(?:[_-]r\d+|[_-]|$)/);
    if (keyMatch) return keyMatch[1].toUpperCase();

    const combined = `${description || ''} ${checkId || ''}`;
    const descMatch = combined.match(/\bSCHEDULE[\s_-]*([A-Za-z])\b/i);
    if (descMatch) return descMatch[1].toUpperCase();

    return 'General';
  };

  const groupedClientAdequacyChecks = useMemo(() => {
    const checks = auditResults?.client_adequacy_checks || [];
    // Group by checklist_item_number when available, otherwise fall back to schedule+index
    const grouped = new Map();

    checks.forEach((check) => {
      const schedule = normalizeScheduleLabel(check.schedule, check.check_id, check.description);
      const itemNum = check.checklist_item_number;
      const groupKey = itemNum != null ? `${schedule}__item__${itemNum}` : `${schedule}__noitem__${check.check_id}`;
      if (!grouped.has(groupKey)) {
        grouped.set(groupKey, {
          groupKey,
          schedule,
          checklist_item_number: itemNum,
          checklist_item_question: check.checklist_item_question || null,
          checks: [],
        });
      }
      grouped.get(groupKey).checks.push(check);
    });

    const groups = Array.from(grouped.values()).map((group) => {
      const failedChecks = group.checks.filter(c => c.status !== 'PASS');
      return {
        ...group,
        overallStatus: failedChecks.length === 0 ? 'PASS' : 'FAIL',
        failedChecks,
        passedCount: group.checks.length - failedChecks.length,
      };
    });

    // Sort: failures first, then by schedule order, then by checklist item number
    const scheduleOrder = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
    groups.sort((a, b) => {
      if (a.overallStatus !== b.overallStatus) return a.overallStatus === 'FAIL' ? -1 : 1;
      const ai = scheduleOrder.indexOf(a.schedule);
      const bi = scheduleOrder.indexOf(b.schedule);
      if (ai !== bi) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      return (a.checklist_item_number ?? 9999) - (b.checklist_item_number ?? 9999);
    });

    return groups;
  }, [auditResults]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">DCAA Audit Checks</h1>
        <p className="text-gray-600">Automated validation of Schedule compliance and cross-schedule reconciliation</p>
      </div>

      {/* Selection and Action Bar */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Schedule H Calculation
            </label>
            <select
              value={selectedScheduleH}
              onChange={(e) => setSelectedScheduleH(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">-- Select Schedule H --</option>
              {scheduleHList.map((sh) => (
                <option key={sh.schedule_h_id} value={sh.schedule_h_id}>
                  {sh.contract_count} contracts • {sh.gl_id} • {new Date(sh.calculated_at).toLocaleDateString()}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex items-end gap-2">
            <button
              onClick={runAudit}
              disabled={loading || !selectedScheduleH}
              className="w-full bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              {loading ? 'Running Audit...' : 'Run DCAA Audit'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4 flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
            <p className="text-red-700">{error}</p>
          </div>
        )}
      </div>

      {/* Audit Results */}
      {auditResults && (
        <>
          {/* Summary Card */}
          <div className={`rounded-lg shadow p-6 mb-6 border-2 ${
            auditResults.overall_status === 'PASS' ? 'bg-green-50 border-green-300' : 'bg-yellow-50 border-yellow-300'
          }`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {auditResults.overall_status === 'PASS' ? '✅ All Checks Passed' : '⚠️ Some Checks Need Review'}
                </h2>
                <p className="text-gray-600 mt-1">Audit ID: {auditResults.audit_id}</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold text-gray-900">
                  {auditResults.summary.passed}/{auditResults.summary.total_checks}
                </div>
                <div className="text-sm text-gray-600">Checks Passed</div>
              </div>
            </div>

            {/* Actions: Snapshot baseline + CSV export */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={markBaseline}
                className="inline-flex items-center gap-2 bg-gray-800 text-white px-4 py-2 rounded-md hover:bg-gray-900"
              >
                <Flag className="h-4 w-4" /> Snapshot as Baseline
              </button>
              <button
                onClick={exportCSV}
                className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-900 px-4 py-2 rounded-md hover:bg-gray-50"
              >
                <Download className="h-4 w-4" /> Export CSV
              </button>
              <button
                onClick={reparseAdequacyChecks}
                disabled={reparseLoading}
                className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-900 px-4 py-2 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${reparseLoading ? 'animate-spin' : ''}`} />
                {reparseLoading ? 'Refreshing...' : 'Refresh Adequacy Checks'}
              </button>
              {baselineStatus && (
                <span className={`text-sm ${baselineStatus.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                  {baselineStatus.message}
                </span>
              )}
              {reparseStatus && (
                <span className={`text-sm ${reparseStatus.type === 'success' ? 'text-green-700' : 'text-red-700'}`}>
                  {reparseStatus.message}
                </span>
              )}
            </div>
          </div>

          {/* Detailed Check Results */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Detailed Audit Check Results</h2>
              <p className="text-sm text-gray-600 mt-1">Official DCAA compliance validation checks</p>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                {Object.entries(auditResults.audit_results)
                  .sort(([, a], [, b]) => {
                    if (a.status !== b.status) return a.status === 'FAIL' ? -1 : 1;
                    return (a.sort_order ?? 999) - (b.sort_order ?? 999);
                  })
                  .map(([checkId, checkResult]) => (
                  <div
                    key={checkId}
                    className={`border-2 rounded-lg p-4 ${getStatusBg(checkResult.status)}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-start gap-3">
                        {getStatusIcon(checkResult.status)}
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {checkResult.dcaa_ref && (
                              <span className="inline-block px-2 py-0.5 rounded text-xs font-mono font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                                Sch {checkResult.dcaa_ref.replace('.', ' · Item ')}
                              </span>
                            )}
                            <h3 className="font-semibold text-gray-900">{checkResult.description}</h3>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Check ID: {checkId}</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(checkResult.status)}`}>
                        {checkResult.status}
                      </span>
                    </div>

                    {/* Show details based on check type */}
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                      {checkResult.schedule_b_ga_pool !== undefined && (
                        <>
                          <div>
                            <span className="text-gray-600">Schedule B Pool:</span>
                            <div className="font-semibold">{formatCurrency(checkResult.schedule_b_ga_pool)}</div>
                          </div>
                          <div>
                            <span className="text-gray-600">Schedule H Total:</span>
                            <div className="font-semibold">{formatCurrency(checkResult.schedule_h_ga_total)}</div>
                          </div>
                          <div>
                            <span className="text-gray-600">Difference:</span>
                            <div className={`font-semibold ${Math.abs(checkResult.difference) < 100 ? 'text-green-600' : 'text-yellow-600'}`}>
                              {formatCurrency(checkResult.difference)}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-600">Variance %:</span>
                            <div className={`font-semibold ${checkResult.variance_pct < 2 ? 'text-green-600' : 'text-yellow-600'}`}>
                              {checkResult.variance_pct?.toFixed(2)}% {checkResult.variance_pct < 2 && '(< 2% ✓)'}
                            </div>
                          </div>
                        </>
                      )}

                      {checkResult.schedule_c_oh_pools !== undefined && (
                        <>
                          <div>
                            <span className="text-gray-600">Schedule C Pools:</span>
                            <div className="font-semibold">{formatCurrency(checkResult.schedule_c_oh_pools)}</div>
                          </div>
                          <div>
                            <span className="text-gray-600">Schedule H Total:</span>
                            <div className="font-semibold">{formatCurrency(checkResult.schedule_h_oh_total)}</div>
                          </div>
                          <div>
                            <span className="text-gray-600">Difference:</span>
                            <div className={`font-semibold ${Math.abs(checkResult.difference) < 100 ? 'text-green-600' : 'text-yellow-600'}`}>
                              {formatCurrency(checkResult.difference)}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-600">Variance %:</span>
                            <div className={`font-semibold ${checkResult.variance_pct < 2 ? 'text-green-600' : 'text-yellow-600'}`}>
                              {checkResult.variance_pct?.toFixed(2)}% {checkResult.variance_pct < 2 && '(< 2% ✓)'}
                            </div>
                          </div>
                        </>
                      )}

                      {checkResult.gl_direct_labor !== undefined && (
                        <>
                          <div>
                            <span className="text-gray-600">GL Direct Labor:</span>
                            <div className="font-semibold">{formatCurrency(checkResult.gl_direct_labor)}</div>
                          </div>
                          <div>
                            <span className="text-gray-600">Schedule H Total:</span>
                            <div className="font-semibold">{formatCurrency(checkResult.schedule_h_direct_labor)}</div>
                          </div>
                          <div>
                            <span className="text-gray-600">Difference:</span>
                            <div className={`font-semibold ${Math.abs(checkResult.difference) < 100 ? 'text-green-600' : 'text-yellow-600'}`}>
                              {formatCurrency(checkResult.difference)}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-600">Variance %:</span>
                            <div className={`font-semibold ${checkResult.variance_pct < 1 ? 'text-green-600' : 'text-yellow-600'}`}>
                              {checkResult.variance_pct?.toFixed(2)}% {checkResult.variance_pct < 1 && '(< 1% ✓)'}
                            </div>
                          </div>
                        </>
                      )}

                      {checkResult.schedule_a_ga_rate !== undefined && (
                        <>
                          <div>
                            <span className="text-gray-600">Schedule A Rate:</span>
                            <div className="font-semibold">{formatPercentage(checkResult.schedule_a_ga_rate)}</div>
                          </div>
                          <div>
                            <span className="text-gray-600">Schedule H Rate:</span>
                            <div className="font-semibold">{formatPercentage(checkResult.schedule_h_ga_rate)}</div>
                          </div>
                          <div>
                            <span className="text-gray-600">Rates Match:</span>
                            <div className={`font-semibold ${checkResult.rates_match ? 'text-green-600' : 'text-red-600'}`}>
                              {checkResult.rates_match ? 'Yes ✓' : 'No ✗'}
                            </div>
                          </div>
                        </>
                      )}

                      {checkResult.note && (
                        <div className="md:col-span-3">
                          <span className="text-gray-600">Note:</span>
                          <div className="text-sm text-gray-700 mt-1">{checkResult.note}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Client-Specific Adequacy Checks */}
          {auditResults.client_adequacy_checks && auditResults.client_adequacy_checks.length > 0 && (
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="px-6 py-4 border-b border-gray-200 bg-blue-50">
                <h2 className="text-xl font-bold text-blue-900 flex items-center gap-2">
                  <FileText className="h-6 w-6" />
                  Client Adequacy Checks
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-20">Schedule</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">Checklist #</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Checklist Item</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {groupedClientAdequacyChecks.map((group) => {
                      const isExpanded = expandedItems.has(group.groupKey);
                      const isFail = group.overallStatus === 'FAIL';
                      return (
                        <React.Fragment key={group.groupKey}>
                          <tr
                            className={`transition-colors ${isFail ? 'bg-red-50 hover:bg-red-100 cursor-pointer' : 'bg-white hover:bg-gray-50'} ${isFail ? 'cursor-pointer' : ''}`}
                            onClick={() => isFail && toggleExpanded(group.groupKey)}
                          >
                            <td className="px-4 py-3 font-semibold text-gray-900">
                              {group.schedule === 'General' ? '—' : `Sch ${group.schedule}`}
                            </td>
                            <td className="px-4 py-3 font-mono font-semibold text-gray-700">
                              {group.checklist_item_number != null ? `#${group.checklist_item_number}` : '—'}
                            </td>
                            <td className="px-4 py-3 text-gray-800">
                              {group.checklist_item_question || group.checks[0]?.description || '—'}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {isFail ? (
                                  <>
                                    <XCircle className="h-4 w-4 text-red-600" />
                                    <span className="font-semibold text-red-600">FAIL</span>
                                    <span className="ml-1 text-xs text-red-500">
                                      {isExpanded ? '▲' : '▼'}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                    <span className="font-semibold text-green-600">PASS</span>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                          {isFail && isExpanded && (
                            <tr key={`${group.groupKey}__detail`}>
                              <td colSpan={4} className="px-6 pb-3 pt-0 bg-red-50">
                                <div className="border-t border-red-200 pt-3 space-y-2">
                                  <div className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">
                                    Failing sub-checks ({group.failedChecks.length} of {group.checks.length}):
                                  </div>
                                  {group.failedChecks.map((fc) => (
                                    <div key={fc.key} className="flex items-start gap-2 text-sm text-red-800 bg-red-100 rounded px-3 py-2">
                                      <XCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                                      <div>
                                        <div className="font-medium">{fc.description}</div>
                                        {fc.note && <div className="text-xs text-red-600 mt-0.5">{fc.note}</div>}
                                      </div>
                                    </div>
                                  ))}
                                  {group.passedCount > 0 && (
                                    <div className="text-xs text-gray-500 mt-2">
                                      {group.passedCount} sub-check{group.passedCount !== 1 ? 's' : ''} passed.
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {/* Discrepancies Section */}
          {auditResults.discrepancies && auditResults.discrepancies.length > 0 && (
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="px-6 py-4 border-b border-gray-200 bg-red-50">
                <h2 className="text-xl font-bold text-red-900 flex items-center gap-2">
                  <AlertTriangle className="h-6 w-6" />
                  Discrepancies Requiring Attention
                </h2>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  {auditResults.discrepancies.map((disc, idx) => (
                    <div key={idx} className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{disc.check}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            Difference: <span className="font-semibold text-red-700">{formatCurrency(Math.abs(disc.difference))}</span>
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          disc.severity === 'HIGH' ? 'bg-red-200 text-red-800' : 'bg-yellow-200 text-yellow-800'
                        }`}>
                          {disc.severity} PRIORITY
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* DCAA Audit Rules Reference */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="h-6 w-6" />
                DCAA Audit Rules Reference
              </h2>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Schedule A Checks</h3>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li>• Pool amounts tie to Schedules B & C expenses</li>
                    <li>• Intermediate pool bases tie to Schedule D</li>
                    <li>• Final pool bases tie to Schedule E</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Schedule B & C Checks</h3>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li>• G&A pool costs tie to Schedule H</li>
                    <li>• OH pool costs tie to Schedule H</li>
                    <li>• Fringe and OH applied to B&P</li>
                    <li>• Intermediate allocations cross-reference</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Schedule H Checks</h3>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li>• Direct costs tie to GL</li>
                    <li>• Contracts grouped by type with subtotals</li>
                    <li>• Rates from Schedule A used correctly</li>
                    <li>• Government participation calculated</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Tolerance Thresholds</h3>
                  <ul className="text-sm text-gray-700 space-y-2">
                    <li>• Small amounts (&lt;$10K): $100</li>
                    <li>• Large pools (&gt;$10M): $1M or 2%</li>
                    <li>• Medium amounts: 2% or $100</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DCAAAuditPage;
